import React, { memo, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useState } from 'react';
import { github } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import {
    CaretDownIcon,
    CaretUpIcon,
    CheckIcon,
    CopyIcon,
    PencilSimpleIcon,
    ArrowClockwiseIcon,
} from '@phosphor-icons/react';
import copy from 'copy-to-clipboard';
import ReactMarkdown from 'react-markdown';
import SyntaxHighlighter from 'react-syntax-highlighter';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import { Tooltip } from 'react-tooltip';
import rehypeKatex from 'rehype-katex';
import Avatar from '..//Utility/Avatar';
import Lightbox from './components/Lightbox';
import MessageEditor from './components/MessageEditor';
import LoadingAnimation from './components/LoadingAnimation';
import AssistantAudioControls from './components/AssistantAudioControls';
import { githubdark } from '../Utility/HLJS/github-dark';
import { Button } from '../Utility/Button';
import { Badge } from '../Utility/Badge';
import { LoadingAnimation as LoadingAnimationType } from '../../Types';
import { setMessageSource } from '../../Store/features/messageSource/messageSourceSlice';
import { StoreMessageSource } from '../../Types/StoreTypes';
import { ChatMessageRole } from '../../Types/Enums';
import { IconName } from '../../SocketProvider';
import { setIsSending } from '../../Store/features/chat/chatSlice';
import { RootState, useAppDispatch, useAppSelector } from '../../store';
import useSocketMessageHandler from '../../Hooks/useSocketMessageHandler';
import { getBreakpoint } from '../../utils/breakpoint';

export type ChatMessageType = {
    identifier: string;
    role: string;
    content: string;
    images: { url: string; sha1: string }[];
    files: { name: string; sha1: string }[];
    contentChanged: boolean;
    originalContent: string;
};

const ChatMessage = ({
    role,
    assistantName,
    assistantImage,
    identifier,
    content,
    images,
    files,
    contentChanged,
    originalContent,
    isLastMessage = false,
    sliceMessages,
    dlpActive,
    socket,
    loadingAnimation,
    isPreviewPrompt = false,
    dlpState = 'dlp_optional',
    sources = [{ title: '', description: '', url: '' }],
    isSending,
    extraHeight = 0,
    shouldGrow = false,
    chatId,
}: {
    role: string;
    assistantName: string;
    assistantImage: string;
    identifier: string;
    content: string;
    images: { url: string; sha1: string }[];
    files: { name: string; sha1: string }[];
    contentChanged: boolean;
    originalContent: string;
    isLastMessage: boolean;
    sliceMessages: any;
    dlpActive: boolean;
    socket: any;
    loadingAnimation: LoadingAnimationType;
    isPreviewPrompt?: boolean;
    dlpState: 'dlp_on' | 'dlp_optional' | 'dlp_off';
    sources: { title: string; description: string; url: string }[];
    isSending: boolean;
    extraHeight?: number;
    shouldGrow?: boolean;
    chatId?: string;
}) => {
    const { SocketMessageHandlerActions } = useSocketMessageHandler();
    const userSettings = useAppSelector((state: RootState) => state.userSettings);
    const i18n = useAppSelector((state: RootState) => state.i18n.i18n);
    const selectedLanguage = useAppSelector((state: RootState) => state.i18n.selectedLanguage);
    const selectedTheme = useAppSelector((state: RootState) => (state as any).themes?.selectedTheme || '');
    const themes = useAppSelector((state: RootState) => (state as any).themes?.allThemes || {});
    const windowWidth = useAppSelector((state: RootState) => state.environment.windowWidth);
    const themeColor = themes?.[selectedTheme]?.themeColor ?? 'light';
    const breakpointSM = getBreakpoint('sm');
    const [copied, setCopied] = useState({ [identifier + 0]: false });
    const [showAssistantCode, setShowAssistantCode] = useState({ [identifier + 0]: false });
    const [showError, setShowError] = useState({ [identifier + 0]: false });
    const [showThinking, setShowThinking] = useState<{ [key: string]: boolean }>({});
    const [lightboxImage, setLightboxImage] = useState<string | null>(null);
    const [editedChatMessage, setEditedChatMessage] = useState(originalContent);
    const [editableMessage, setEditableMessage] = useState('');
    const dispatch = useAppDispatch();
    const messageSourceIdentifier = useAppSelector((state: RootState) => state.messageSource.messageIdentifier);
    const [slidePosition, setSlidePosition] = useState(0);
    const [touchStart, setTouchStart] = useState(0);
    const [isSwiping, setIsSwiping] = useState(false);
    const [isMouseDown, setIsMouseDown] = useState(false);
    const [fullCopied, setFullCopied] = useState(false);
    const globalSettings = useAppSelector((state: RootState) => state.settings);

    const hasAssistantCode = useMemo(() => {
        if (!content) return false;
        const contentWithoutAssistantCode = content
            .replace(/```assistantcode[\s\S]*?```/g, '')
            .replace(/```assistantcode[\s\S]*$/g, '')
            .trim();
        return contentWithoutAssistantCode !== '';
    }, [content]);

    const safeUrlTransform = (url: string) => {
        if (!url) return url;
        const trimmed = url.trim();
        if (trimmed.startsWith('#') || trimmed.startsWith('/')) return trimmed;

        try {
            const parsed = new URL(trimmed, window.location.origin);
            const protocol = parsed.protocol.toLowerCase();
            const defaultAllowed = ['http', 'https', 'mailto', 'tel', 'sandbox', 'data'];
            const settingsAllowed = globalSettings?.allowedProtocols || defaultAllowed;
            const additional = globalSettings?.additionalAllowedProtocols || [];
            const allowedSet = Array.from(new Set<string>([...settingsAllowed, ...additional]));
            const allowed = allowedSet.map((p: string) => (p.endsWith(':') ? p.toLowerCase() : p.toLowerCase() + ':'));
            if (allowed.includes(protocol)) {
                return trimmed;
            }
        } catch (e) {
            if (!/^[a-zA-Z][a-zA-Z0-9+\-.]*:/.test(trimmed)) {
                return trimmed;
            }
        }

        return '';
    };

    useEffect(() => {
        setIsSwiping(false);
        setSlidePosition(0);
    }, [identifier]);

    useEffect(() => {
        if (messageSourceIdentifier === identifier) {
            dispatch(
                setMessageSource({
                    messageIdentifier: identifier,
                    content: sources,
                } as StoreMessageSource),
            );
        }
    }, [sources, dispatch, messageSourceIdentifier, identifier]);

    useEffect(() => {
        if (windowWidth >= breakpointSM) {
            setSlidePosition(0);
            setIsSwiping(false);
            setIsMouseDown(false);
        }
    }, [windowWidth, breakpointSM]);

    useLayoutEffect(() => {
        // Trigger resize event to recalculate code block widths on initial load
        const timer = setTimeout(() => {
            window.dispatchEvent(new Event('resize'));
        }, 0);
        return () => clearTimeout(timer);
    }, [content]);

    const editMessageHandler = (messageIdentifier: string, overrideContent?: string) => {
        SocketMessageHandlerActions.mayEditMessage(chatId as string, () => {
            sliceMessages(messageIdentifier);

            let dlpActiveParam = true;
            switch (dlpState) {
                case 'dlp_on':
                    dlpActiveParam = true;
                    break;
                case 'dlp_off':
                    dlpActiveParam = false;
                    break;
                // case 'dlp_optional':
                default:
                    dlpActiveParam = dlpActive ? true : false;
                    // data.append('dlpActive', dlpActive ? 'true' : 'false');
                    break;
            }

            let params = {
                userContent: overrideContent ?? editedChatMessage,
                type: 'editMessage',
                messageIdentifier: messageIdentifier,
                chatIdentifier: chatId,
                dlpActive: dlpActiveParam,
                language: selectedLanguage,
            };

            dispatch(setIsSending(true));
            socket?.send(JSON.stringify(params));
        });
    };

    const handleRetryClick = () => {
        if (role !== 'user') return;
        editMessageHandler(identifier, originalContent);
    };

    const openLightbox = (image: string) => {
        setLightboxImage(image);
    };

    const closeLightbox = () => {
        setLightboxImage(null);
    };

    const handleOverlayClick = (e: any) => {
        if (e.target.id === 'lightbox-overlay') {
            closeLightbox();
        }
    };

    if (typeof content !== 'string') {
        return <></>;
    }
    let internalLinks: { source: string; fileUrl: string }[] = [];

    const sandboxLinks = content.match(/\\?\[sandbox\\?\|[^\]\\]+\\?\]/g);
    if (sandboxLinks) {
        sandboxLinks.forEach((link) => {
            const matchResult = link.match(/\\?\[sandbox\\?\|(.*?)\\?\|(.*?)\\?\]/);
            if (matchResult) {
                const [, source, fileUrl] = matchResult;
                internalLinks.push({ source: source, fileUrl: fileUrl });
            }
            content = content.replace(link, '');
        });
    }

    const fileCitations = content.match(/\\?\[file_citation\\?\|[^\]\\]+\\?\]/g);
    if (fileCitations) {
        fileCitations.forEach((citation) => {
            const matchResult = citation.match(/\\?\[file_citation\\?\|(.*?)\\?\|(.*?)\\?\|(.*?)\\?\|(.*?)\\?\]/);
            if (matchResult) {
                const [, text, quote, fileName, fileUrl] = matchResult;
                content = content.replace(
                    text,
                    (quote ? '"' + quote + '"' : '') + ' [' + fileName + '](' + fileUrl + ')',
                );
            }
            content = content.replace(citation, '');
        });
    }

    internalLinks.forEach((link) => {
        content = content.replace(link.source, link.fileUrl);
    });

    content = content.replace(/<(?:think|thought)>(.*?)<\/(?:think|thought)>/g, (_, p1) => {
        if (!p1) return '```think\n```';
        return '```think\n' + p1.replace(/```/g, '###TRIPPLEBACKTICK###') + '\n```';
    });

    content = content.replace(/<(?:think|thought)>/g, '```think');
    content = content.replace(/<\/(?:think|thought)>/g, '```');

    content = content.replace(/<error>(.*?)<\/error>/g, (_, p1) => {
        if (!p1) return '```error\n```';
        return '```error\n' + p1.replace(/```/g, '###TRIPPLEBACKTICK###') + '\n```';
    });

    content = content.replace('<error>', '```error');
    content = content.replace('</error>', '```');

    content = content.replace(/【(.*)†(.*)】/g, (_, messageLink, linkText) => {
        return '[' + linkText + '](' + messageLink + '†)';
    });

    const handleTouchStart = (e: React.TouchEvent | React.MouseEvent) => {
        if (role !== 'user' || identifier === editableMessage || windowWidth >= breakpointSM) return;

        if ('preventDefault' in e) {
            e.preventDefault();
        }

        if ('touches' in e) {
            const clientX = e.touches[0].clientX;
            setTouchStart(clientX);
            setIsSwiping(true);
        } else {
            const clientX = e.clientX;
            setTouchStart(clientX);
            setIsMouseDown(true);
            setIsSwiping(true);
        }
    };

    const handleTouchMove = (e: React.TouchEvent | React.MouseEvent) => {
        if (role !== 'user' || identifier === editableMessage || windowWidth >= breakpointSM) return;
        if (!('touches' in e) && !isMouseDown) return;
        if (!isSwiping) return;

        if ('preventDefault' in e) {
            e.preventDefault();
        }

        const clientX = 'touches' in e ? e.touches[0].clientX : (e as React.MouseEvent).clientX;
        const diff = touchStart - clientX;

        const currentPosition = slidePosition;
        const targetPosition = currentPosition + diff / 3;
        const newPosition = Math.max(Math.min(targetPosition, 70), 0);

        setTouchStart(clientX);
        setSlidePosition(newPosition);
    };

    const handleTouchEnd = () => {
        if (!isSwiping || role !== 'user') return;

        setIsMouseDown(false);

        if (slidePosition > 35) {
            requestAnimationFrame(() => {
                setSlidePosition(70);
                setIsSwiping(false);
            });
        } else {
            requestAnimationFrame(() => {
                setIsSwiping(false);
                setSlidePosition(0);
            });
        }
    };

    const handleEditClick = () => {
        if (identifier === editableMessage) return;

        setEditedChatMessage(originalContent);
        setEditableMessage(identifier);

        requestAnimationFrame(() => {
            setSlidePosition(0);
            requestAnimationFrame(() => {
                setIsSwiping(false);
            });
        });

        const messageElement = document.querySelector(`[data-message-id="${identifier}"]`);
        if (messageElement) {
            messageElement.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
        }
    };

    const analyzeContentSegments = () => {
        const segments: Array<{ type: 'content' | 'think'; data: any; index: number }> = [];
        const thinkBlockRegex = /```think[\s\S]*?(?:```|$)/g;
        let lastIndex = 0;
        let match;
        let thinkBlockIndex = 0;

        while ((match = thinkBlockRegex.exec(content)) !== null) {
            if (match.index > lastIndex) {
                const contentBefore = content.slice(lastIndex, match.index);
                if (contentBefore.trim()) {
                    segments.push({
                        type: 'content',
                        data: contentBefore,
                        index: segments.length,
                    });
                }
            }

            const blockContent = match[0].replace(/```think\n?/, '').replace(/```$/, '');
            const isComplete = match[0].endsWith('```');
            const allThinkBlocks = content.match(/```think[\s\S]*?(?:```|$)/g) || [];
            const isLast = thinkBlockIndex === allThinkBlocks.length - 1;

            segments.push({
                type: 'think',
                data: {
                    id: `${identifier}-think-${thinkBlockIndex}`,
                    content: blockContent,
                    isComplete,
                    isLast,
                    index: thinkBlockIndex,
                },
                index: segments.length,
            });

            lastIndex = match.index + match[0].length;
            thinkBlockIndex++;
        }

        if (lastIndex < content.length) {
            const contentAfter = content.slice(lastIndex);
            if (contentAfter.trim()) {
                segments.push({
                    type: 'content',
                    data: contentAfter,
                    index: segments.length,
                });
            }
        }

        if (segments.length === 0 && content.trim()) {
            segments.push({
                type: 'content',
                data: content,
                index: 0,
            });
        }

        return segments;
    };

    const contentSegments = analyzeContentSegments();
    const thinkBlocks = contentSegments.filter((s) => s.type === 'think').map((s) => s.data);

    const renderThinkBlock = (
        block: { id: string; content: string; isComplete: boolean; isLast: boolean },
        index: number,
    ) => {
        const isVisible = showThinking[block.id] || false;
        const showAnimation = !block.isComplete && block.isLast && isLastMessage && isSending;

        return (
            <div key={block.id} className="my-4">
                <span
                    className="block mt-4 mb-2 hover:no-underline hover:cursor-pointer text-gray-500"
                    onClick={() =>
                        setShowThinking((prevState) => ({
                            ...prevState,
                            [block.id]: !isVisible,
                        }))
                    }
                >
                    {isVisible ? (
                        <div className="flex items-center">
                            <span>
                                {i18n.translate({
                                    id: 'chatMessage.hideThinking',
                                    sourceName: 'Chat',
                                })}{' '}
                                {thinkBlocks.length > 1 ? `(${index + 1})` : ''}
                            </span>
                            <CaretUpIcon className="ml-2 shrink-0" size={18} />
                        </div>
                    ) : (
                        <div className="flex items-center">
                            <span>
                                {i18n.translate({
                                    id: 'chatMessage.thinking',
                                    sourceName: 'Chat',
                                })}{' '}
                                {thinkBlocks.length > 1 ? `(${index + 1})` : ''}
                            </span>
                            <CaretDownIcon className="ml-2 shrink-0" size={18} />
                            {showAnimation && (
                                <div className="ml-3 flex items-center">
                                    <div className="thinking-dots">
                                        <div className="thinking-dot"></div>
                                        <div className="thinking-dot"></div>
                                        <div className="thinking-dot"></div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </span>
                <div className={`${!isVisible ? 'hidden' : ''} px-5 mt-1 mb-3 border-l-2 border-layout-borderColor`}>
                    <div className="text-gray-500">
                        <ReactMarkdown
                            remarkPlugins={[
                                remarkGfm,
                                remarkBreaks,
                                [
                                    remarkMath,
                                    {
                                        // Enable LaTeX math expressions: $...$ for inline and $$...$$ for display math
                                        singleDollarTextMath: true,
                                        doubleDollarTextMath: true,
                                    },
                                ],
                            ]}
                            rehypePlugins={[[rehypeKatex, { output: 'html' }]]}
                            urlTransform={safeUrlTransform}
                            components={{
                                pre({ children }) {
                                    const child = React.Children.only(children) as React.ReactElement;
                                    if (typeof child.type === 'function' && child.type.name === 'code') {
                                        const { children, className } = child.props as {
                                            children: React.ReactNode;
                                            className?: string;
                                        };
                                        const handleCopy = () => {
                                            copy(String(children));
                                            setCopied((prev) => ({
                                                ...prev,
                                                [block.id + String(children)]: true,
                                            }));
                                            setTimeout(
                                                () =>
                                                    setCopied((prev) => ({
                                                        ...prev,
                                                        [block.id + String(children)]: false,
                                                    })),
                                                1500,
                                            );
                                        };

                                        let match = /language-(\w+)/.exec(className || '');
                                        let stringChildren = String(children);

                                        return (
                                            <div 
                                                className="rounded-lg overflow-x-auto overflow-y-hidden text-layout-textcolor border border-layout-borderColor bg-chat-codeBackground relative my-3 w-full max-w-full min-w-0"
                                            >
                                                <button
                                                    className="copy-button w-[30px] h-[30px] bg-transparent text-layout-textcolor border-none rounded-s cursor-pointer top-[5px] right-[5px] z-[1] absolute flex justify-center items-center transition duration-300 hover:text-primary"
                                                    onClick={handleCopy}
                                                >
                                                    {copied[block.id + String(children)] ? (
                                                        <CheckIcon size={16} />
                                                    ) : (
                                                        <CopyIcon size={16} />
                                                    )}
                                                </button>
                                                <SyntaxHighlighter
                                                    {...(child.props as {
                                                        style: any;
                                                        language: string;
                                                        PreTag: string | React.JSXElementConstructor<any>;
                                                    })}
                                                    children={stringChildren}
                                                    customStyle={{
                                                        padding: '1.5rem',
                                                        overflowX: 'auto',
                                                        width: '100%',
                                                        maxWidth: '100%'
                                                    }}
                                                    style={
                                                        themeColor === 'light' ? (github as any) : (githubdark as any)
                                                    }
                                                    language={match ? match[1] : 'text'}
                                                    PreTag="div"
                                                    wrapLongLines={false}
                                                />
                                            </div>
                                        );
                                    }
                                    return <>{children}</>;
                                },
                                img({ node, src, ...props }) {
                                    return (
                                        <img
                                            {...props}
                                            onClick={() => openLightbox(src ?? '')}
                                            src={src}
                                            className="max-h-[300px] rounded-xl inline-block my-2 cursor-pointer"
                                            alt=""
                                        />
                                    );
                                },
                                a({ node, href, ...props }) {
                                    return href?.match(/\.(jpeg|jpg|gif|png)$/) ? (
                                        <span {...props} />
                                    ) : (
                                        <a
                                            href={href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            aria-label=""
                                            {...props}
                                        />
                                    );
                                },
                                code({ node, className, children, ...props }) {
                                    return (
                                        <code {...props} className={className}>
                                            {children}
                                        </code>
                                    );
                                },
                                table({ node, ...props }) {
                                    return (
                                        <div className="overflow-x-auto py-[10px] px-[20px] my-[10px] table-container rounded-lg border border-layout-borderColor bg-chat-TabletBackground">
                                            <table className="chattable w-full" {...props} />
                                        </div>
                                    );
                                },
                                tr({ node, ...props }) {
                                    return <tr className="" {...props} />;
                                },
                                th({ node, ...props }) {
                                    return <th className="px-4 py-3 text-left" {...props} />;
                                },
                                td({ node, ...props }) {
                                    return <td className="px-4 py-3 text-left border-t" {...props} />;
                                },
                                h1({ node, children, ...props }) {
                                    return (
                                        <h1 className="mt-3 sm:mt-4 text-lg" {...props}>
                                            {children}
                                        </h1>
                                    );
                                },
                                h2({ node, children, ...props }) {
                                    return (
                                        <h2 className="mt-3 sm:mt-4 text-base" {...props}>
                                            {children}
                                        </h2>
                                    );
                                },
                                h3({ node, children, ...props }) {
                                    return (
                                        <h3 className="mt-2 sm:mt-3 text-sm" {...props}>
                                            {children}
                                        </h3>
                                    );
                                },
                                h4({ node, children, ...props }) {
                                    return (
                                        <h4 className="mt-2 text-sm" {...props}>
                                            {children}
                                        </h4>
                                    );
                                },
                                h5({ node, children, ...props }) {
                                    return (
                                        <h5 className="mt-1 text-sm" {...props}>
                                            {children}
                                        </h5>
                                    );
                                },
                                h6({ node, children, ...props }) {
                                    return (
                                        <h6 className="mt-1 text-sm" {...props}>
                                            {children}
                                        </h6>
                                    );
                                },
                                p({ node, children, ...props }) {
                                    return (
                                        <p className="break-words mb-2" {...props}>
                                            {children}
                                        </p>
                                    );
                                },
                            }}
                        >
                            {block.content
                                .replace(/\\\[(.*?)\\\]/g, (_, equation) => `$$${equation}$$`)
                                .replace(/\\\((.*?)\\\)/g, (_, equation) => `$${equation}$`)}
                        </ReactMarkdown>
                    </div>
                </div>
            </div>
        );
    };
    const minHeightClass = shouldGrow
        ? isPreviewPrompt
            ? 'min-h-[calc(100dvh-500px)]'
            : contentChanged
              ? 'min-h-[calc(100dvh-350px)] xl:min-h-[calc(100dvh-385px)]'
              : 'min-h-[calc(100dvh-300px)] xl:min-h-[calc(100dvh-350px)]'
        : 'min-h-0';

    return (
        <>
            {lightboxImage && (
                <Lightbox
                    lightboxImage={lightboxImage}
                    handleOverlayClick={handleOverlayClick}
                    closeLightbox={closeLightbox}
                />
            )}
            <div
                data-message-id={identifier}
                className={`lastelementTest message group py-2 overflow-hidden ${
                    isPreviewPrompt ? '' : 'px-3 sm:px-10'
                } ${role} w-full ${minHeightClass}`}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onTouchCancel={handleTouchEnd}
                onMouseDown={handleTouchStart}
                onMouseMove={handleTouchMove}
                onMouseUp={handleTouchEnd}
                onMouseLeave={handleTouchEnd}
            >
                <div className="min-w-0 w-full">
                    <div
                        className={`relative group chat-message-container pl-[45px] sm:px-[50px] min-w-0 w-full`}
                        style={{
                            transform: `translateX(-${slidePosition}px)`,
                            transition: isSwiping ? 'none' : 'transform 0.3s ease-out',
                        }}
                    >
                        <div
                            className="absolute inset-0 bg-gray-900 pointer-events-none"
                            style={{
                                opacity: slidePosition / 70,
                                transition: isSwiping ? 'none' : 'opacity 0.3s ease-out',
                            }}
                        />

                        {role === 'user' && (
                            <div
                                className={`absolute block sm:hidden cursor-pointer top-0 right-[-70px] h-full bg-primary flex items-center justify-center w-[85px]`}
                                style={{
                                    opacity: slidePosition / 70,
                                    transform: `translateX(${0 + slidePosition / 5}px)`,
                                    transition: isSwiping ? 'none' : 'all 0.3s ease-out',
                                }}
                                onClick={handleEditClick}
                            >
                                <PencilSimpleIcon size={18} className="pointer-events-none text-secondary" />
                            </div>
                        )}

                        <Avatar
                            role={role as ChatMessageRole}
                            size={32}
                            avatarImage={assistantImage}
                            title={assistantName}
                        />
                        <div className={`flex mb-0 pt-1`}>
                            {role === 'assistant' ? (
                                <p className="max-w-full truncate font-[600] font-[20px] leading-[20px] p-0 d-block text-chatMessage-roleColor">
                                    {assistantName}
                                </p>
                            ) : (
                                <p className="max-w-full truncate font-[600] font-[20px] leading-[20px] p-0 d-block text-chatMessage-roleColor">
                                    {userSettings.givenName} {userSettings.familyName}
                                </p>
                            )}
                        </div>
                        {images && (
                            <div className="max-w-full flex flex-wrap items-start justify-start">
                                {images.map((image: any, index: number) =>
                                    image.url?.match(/^data:image/) ? (
                                        <div
                                            key={index}
                                            className={`${
                                                role === 'assistant' ? 'w-[400px] h-[400px]' : 'w-[150px] h-[150px]'
                                            } bg-chatMessage-image-background flex items-center justify-center mr-2 mb-2 cursor-pointer`}
                                            onClick={() => openLightbox(image.url)}
                                        >
                                            <img
                                                key={index}
                                                src={image.url}
                                                className="max-w-full max-h-full object-cover"
                                                alt=""
                                            />
                                        </div>
                                    ) : (
                                        <span
                                            key={index}
                                            className="border bg-chatMessage-image-background rounded-xl p-3 my-2"
                                        >
                                            {image.name}
                                        </span>
                                    ),
                                )}
                            </div>
                        )}
                        {content && content.trim() !== '' ? (
                            <>
                                {role === 'user' && files && (
                                    <div className="max-w-full flex flex-wrap items-start justify-start">
                                        {files.map((file: any, index: number) =>
                                            file.mediaType?.startsWith('image/') || file.url?.match(/^data:image/) ? (
                                                <div
                                                    key={index}
                                                    className="w-[150px] h-[150px] bg-chatMessage-image-background flex items-center justify-center mr-2 mb-2 cursor-pointer"
                                                    onClick={() => openLightbox(file.url)}
                                                >
                                                    <img
                                                        key={index}
                                                        src={file.url}
                                                        className="max-w-full max-h-full object-cover"
                                                        alt=""
                                                    />
                                                </div>
                                            ) : (
                                                <span
                                                    key={index}
                                                    className="border bg-chatMessage-image-background rounded-xl p-3 my-2"
                                                >
                                                    {file.name}
                                                </span>
                                            ),
                                        )}
                                    </div>
                                )}
                                {role === 'assistant' ? (
                                    <>
                                        {/* Render content segments in correct order */}
                                        {contentSegments.map((segment) => {
                                            if (segment.type === 'think') {
                                                return renderThinkBlock(segment.data, segment.data.index);
                                            } else {
                                                return (
                                                    <div key={`content-${segment.index}`}>
                                                        <ReactMarkdown
                                                            remarkPlugins={[
                                                                remarkGfm,
                                                                remarkBreaks,
                                                                [
                                                                    remarkMath,
                                                                    {
                                                                        // Enable LaTeX math expressions: $...$ for inline and $$...$$ for display math
                                                                        singleDollarTextMath: true,
                                                                        doubleDollarTextMath: true,
                                                                    },
                                                                ],
                                                            ]}
                                                            rehypePlugins={[[rehypeKatex, { output: 'html' }]]}
                                                            urlTransform={safeUrlTransform}
                                                            components={{
                                                                pre({ children }) {
                                                                    const child = React.Children.only(
                                                                        children,
                                                                    ) as React.ReactElement;
                                                                    if (
                                                                        typeof child.type === 'function' &&
                                                                        child.type.name === 'code'
                                                                    ) {
                                                                        const { children, className } = child.props as {
                                                                            children: React.ReactNode;
                                                                            className?: string;
                                                                        };
                                                                        const handleCopy = () => {
                                                                            copy(childrenParts[0]);
                                                                            setCopied((prev) => ({
                                                                                ...prev,
                                                                                [identifier + String(children)]: true,
                                                                            }));
                                                                            setTimeout(
                                                                                () =>
                                                                                    setCopied((prev) => ({
                                                                                        ...prev,
                                                                                        [identifier + String(children)]:
                                                                                            false,
                                                                                    })),
                                                                                1500,
                                                                            );
                                                                        };

                                                                        let match = /language-(\w+)/.exec(
                                                                            className || '',
                                                                        );

                                                                        let stringChildren = String(children);
                                                                        stringChildren = stringChildren.replace(
                                                                            /###TRIPPLEBACKTICK###/g,
                                                                            '```',
                                                                        );
                                                                        let childrenParts = [stringChildren];
                                                                        let isAssistantCode = false;

                                                                        let isError = false;
                                                                        if (match && match[1] === 'assistantcode') {
                                                                            isAssistantCode = true;
                                                                            match[1] = 'python';
                                                                            stringChildren = stringChildren.replace(
                                                                                /# Code\n/g,
                                                                                '',
                                                                            );
                                                                            childrenParts =
                                                                                stringChildren.split('# Output\n');
                                                                        }

                                                                        if (match && match[1] === 'error') {
                                                                            isAssistantCode = false;
                                                                            isError = true;
                                                                            match[1] = 'python';
                                                                            stringChildren = stringChildren.replace(
                                                                                /# Code\n/g,
                                                                                '',
                                                                            );
                                                                            childrenParts =
                                                                                stringChildren.split('# Output\n');
                                                                            childrenParts[0] = childrenParts[0].trim();
                                                                        }

                                                                        if (
                                                                            childrenParts[0] === '' ||
                                                                            childrenParts[0] === undefined ||
                                                                            childrenParts[0] === 'undefined'
                                                                        ) {
                                                                            return <></>;
                                                                        }
                                                                        return (
                                                                            <>
                                                                                {isError ? (
                                                                                    <>
                                                                                        <span
                                                                                            className="block mt-4 mb-2 hover:no-underline hover:cursor-pointer text-gray-500"
                                                                                            onClick={() =>
                                                                                                setShowError(
                                                                                                    (prevState) => ({
                                                                                                        ...prevState,
                                                                                                        [identifier]:
                                                                                                            !showError[
                                                                                                                identifier
                                                                                                            ],
                                                                                                    }),
                                                                                                )
                                                                                            }
                                                                                        >
                                                                                            {showError[identifier] ? (
                                                                                                <div className="flex items-center">
                                                                                                    <span>
                                                                                                        {i18n.translate(
                                                                                                            {
                                                                                                                id: 'chatMessage.hideError',
                                                                                                                sourceName:
                                                                                                                    'Chat',
                                                                                                            },
                                                                                                        )}
                                                                                                    </span>
                                                                                                    <CaretUpIcon
                                                                                                        className="ml-2 shrink-0"
                                                                                                        size={18}
                                                                                                    />
                                                                                                </div>
                                                                                            ) : (
                                                                                                <div className="flex items-center">
                                                                                                    <span>
                                                                                                        {i18n.translate(
                                                                                                            {
                                                                                                                id: 'chatMessage.showError',
                                                                                                                sourceName:
                                                                                                                    'Chat',
                                                                                                            },
                                                                                                        )}
                                                                                                    </span>
                                                                                                    <CaretDownIcon
                                                                                                        className="ml-2 shrink-0"
                                                                                                        size={18}
                                                                                                    />
                                                                                                </div>
                                                                                            )}
                                                                                        </span>
                                                                                        <div
                                                                                            className={`${
                                                                                                !showError[identifier]
                                                                                                    ? 'hidden'
                                                                                                    : ''
                                                                                            } px-5 mt-1 mb-3 border-l-2 border-layout-borderColor text-gray-500 whitespace-pre-wrap`}
                                                                                        >
                                                                                            {childrenParts[0]}
                                                                                        </div>
                                                                                    </>
                                                                                ) : (
                                                                                    <>
                                                                                        {isAssistantCode && (
                                                                                            <span
                                                                                                className="block my-4 hover:no-underline hover:cursor-pointer"
                                                                                                onClick={() =>
                                                                                                    setShowAssistantCode(
                                                                                                        (
                                                                                                            prevState,
                                                                                                        ) => ({
                                                                                                            ...prevState,
                                                                                                            [identifier]:
                                                                                                                !showAssistantCode[
                                                                                                                    identifier
                                                                                                                ],
                                                                                                        }),
                                                                                                    )
                                                                                                }
                                                                                            >
                                                                                                {showAssistantCode[
                                                                                                    identifier
                                                                                                ] ? (
                                                                                                    <>
                                                                                                        <CaretUpIcon
                                                                                                            className="inline-block"
                                                                                                            size={24}
                                                                                                        />
                                                                                                        {i18n.translate(
                                                                                                            {
                                                                                                                id: 'chatMessage.hideAnalysis',
                                                                                                                sourceName:
                                                                                                                    'Chat',
                                                                                                            },
                                                                                                        )}
                                                                                                    </>
                                                                                                ) : (
                                                                                                    <>
                                                                                                        <CaretDownIcon
                                                                                                            className="inline-block"
                                                                                                            size={24}
                                                                                                        />
                                                                                                        {i18n.translate(
                                                                                                            {
                                                                                                                id: 'chatMessage.showAnalysis',
                                                                                                                sourceName:
                                                                                                                    'Chat',
                                                                                                            },
                                                                                                        )}
                                                                                                    </>
                                                                                                )}
                                                                                            </span>
                                                                                        )}
                                                                                        <div
                                                                                            className={`rounded-lg overflow-x-auto overflow-y-hidden text-layout-textcolor border border-layout-borderColor bg-chat-codeBackground relative w-full max-w-full min-w-0 ${
                                                                                                isAssistantCode
                                                                                                    ? 'mt-2 mb-4'
                                                                                                    : 'my-4'
                                                                                            } ${
                                                                                                !isAssistantCode ||
                                                                                                showAssistantCode[
                                                                                                    identifier
                                                                                                ]
                                                                                                    ? 'block'
                                                                                                    : 'hidden'
                                                                                            }`}
                                                                                        >
                                                                                            <button
                                                                                                className="copy-button w-[30px] h-[30px] bg-transparent text-layout-textcolor border-none rounded-s cursor-pointer top-[5px] right-[5px] z-[1] absolute flex justify-center items-center transition duration-300 hover:text-primary"
                                                                                                onClick={handleCopy}
                                                                                            >
                                                                                                {copied[
                                                                                                    identifier +
                                                                                                        String(children)
                                                                                                ] ? (
                                                                                                    <CheckIcon
                                                                                                        size={24}
                                                                                                    />
                                                                                                ) : (
                                                                                                    <CopyIcon
                                                                                                        size={24}
                                                                                                    />
                                                                                                )}
                                                                                            </button>
                                                                                            <SyntaxHighlighter
                                                                                                {...(child.props as {
                                                                                                    style: any;
                                                                                                    language: string;
                                                                                                    PreTag:
                                                                                                        | string
                                                                                                        | React.JSXElementConstructor<any>;
                                                                                                })}
                                                                                                children={
                                                                                                    childrenParts[0]
                                                                                                }
                                                                                                customStyle={{
                                                                                                    padding: '1.5rem',
                                                                                                    overflowX: 'auto',
                                                                                                    width: '100%',
                                                                                                    maxWidth: '100%'
                                                                                                }}
                                                                                                style={
                                                                                                    themeColor ===
                                                                                                    'light'
                                                                                                        ? (github as any)
                                                                                                        : (githubdark as any)
                                                                                                }
                                                                                                language={
                                                                                                    match
                                                                                                        ? match[1]
                                                                                                        : 'text'
                                                                                                }
                                                                                                PreTag="div"
                                                                                                wrapLongLines={false}
                                                                                            />
                                                                                            {childrenParts.length >
                                                                                                1 && (
                                                                                                <div
                                                                                                    className={`${
                                                                                                        themeColor ===
                                                                                                        'light'
                                                                                                            ? 'bg-gray-200'
                                                                                                            : 'bg-gray-900'
                                                                                                    } p-3 border-t border-layout-borderColor`}
                                                                                                >
                                                                                                    <span className="text-sm text-gray-400">
                                                                                                        {i18n.translate(
                                                                                                            {
                                                                                                                id: 'chatMessage.output',
                                                                                                                sourceName:
                                                                                                                    'Chat',
                                                                                                            },
                                                                                                        )}
                                                                                                    </span>
                                                                                                    <br />
                                                                                                    <code>
                                                                                                        {
                                                                                                            childrenParts[1]
                                                                                                        }
                                                                                                    </code>
                                                                                                </div>
                                                                                            )}
                                                                                        </div>
                                                                                    </>
                                                                                )}
                                                                            </>
                                                                        );
                                                                    }
                                                                    return <>{children}</>;
                                                                },
                                                                img({ node, src, ...props }) {
                                                                    return (
                                                                        <img
                                                                            {...props}
                                                                            onClick={() => openLightbox(src ?? '')}
                                                                            src={src}
                                                                            className="max-h-[400px] rounded-xl inline-block my-2 cursor-pointer"
                                                                            alt=""
                                                                        />
                                                                    );
                                                                },
                                                                a({ node, href, ...props }) {
                                                                    if (
                                                                        href?.match(/(.*)†$/) ||
                                                                        href?.match(/(.*)%E2%80%A0$/)
                                                                    ) {
                                                                        return (
                                                                            <Badge variant="citation">
                                                                                <a
                                                                                    className="no-underline"
                                                                                    target="_blank"
                                                                                    rel="noopener noreferrer"
                                                                                    href={href
                                                                                        .replace('†', '')
                                                                                        .replace('%E2%80%A0', '')}
                                                                                >
                                                                                    {props.children}
                                                                                </a>
                                                                            </Badge>
                                                                        );
                                                                    }

                                                                    return href?.match(/\.(jpeg|jpg|gif|png)$/) ? (
                                                                        <span {...props} />
                                                                    ) : href?.match(
                                                                          /^https:\/\/sandbox\.example\.com/,
                                                                      ) ? (
                                                                        <a
                                                                            href={href.replace(
                                                                                'https://sandbox.example.com/',
                                                                                'sandbox:/',
                                                                            )}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            aria-label=""
                                                                            {...props}
                                                                        />
                                                                    ) : (
                                                                        <a
                                                                            href={href}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            aria-label=""
                                                                            {...props}
                                                                        />
                                                                    );
                                                                },
                                                                code({ node, className, children, ...props }) {
                                                                    return (
                                                                        <code {...props} className={className}>
                                                                            {children}
                                                                        </code>
                                                                    );
                                                                },
                                                                table({ node, ...props }) {
                                                                    return (
                                                                        <div className="overflow-x-auto py-[10px] px-[20px] my-[10px] table-container rounded-lg border border-layout-borderColor bg-chat-TabletBackground">
                                                                            <table
                                                                                className="chattable w-full"
                                                                                {...props}
                                                                            />
                                                                        </div>
                                                                    );
                                                                },
                                                                tr({ node, ...props }) {
                                                                    return <tr className="" {...props} />;
                                                                },
                                                                th({ node, ...props }) {
                                                                    return (
                                                                        <th
                                                                            className="px-4 py-3 text-left"
                                                                            {...props}
                                                                        />
                                                                    );
                                                                },
                                                                td({ node, ...props }) {
                                                                    return (
                                                                        <td
                                                                            className="px-4 py-3 text-left border-t"
                                                                            {...props}
                                                                        />
                                                                    );
                                                                },
                                                                h1({ node, children, ...props }) {
                                                                    return (
                                                                        <h1
                                                                            className="mt-4 sm:mt-6 text-chat-h1"
                                                                            {...props}
                                                                        >
                                                                            {children}
                                                                        </h1>
                                                                    );
                                                                },
                                                                h2({ node, children, ...props }) {
                                                                    return (
                                                                        <h2
                                                                            className="mt-4 sm:mt-6 text-chat-h2"
                                                                            {...props}
                                                                        >
                                                                            {children}
                                                                        </h2>
                                                                    );
                                                                },
                                                                h3({ node, children, ...props }) {
                                                                    return (
                                                                        <h3
                                                                            className="mt-4 sm:mt-6 text-chat-h3"
                                                                            {...props}
                                                                        >
                                                                            {children}
                                                                        </h3>
                                                                    );
                                                                },
                                                                h4({ node, children, ...props }) {
                                                                    return (
                                                                        <h4
                                                                            className="mt-4 sm:mt-6 text-chat-h4"
                                                                            {...props}
                                                                        >
                                                                            {children}
                                                                        </h4>
                                                                    );
                                                                },
                                                                h5({ node, children, ...props }) {
                                                                    return (
                                                                        <h5
                                                                            className="mt-4 sm:mt-6 text-chat-h5"
                                                                            {...props}
                                                                        >
                                                                            {children}
                                                                        </h5>
                                                                    );
                                                                },
                                                                h6({ node, children, ...props }) {
                                                                    return (
                                                                        <h6
                                                                            className="mt-4 sm:mt-6 text-chat-h6"
                                                                            {...props}
                                                                        >
                                                                            {children}
                                                                        </h6>
                                                                    );
                                                                },
                                                                p({ node, children, ...props }) {
                                                                    return (
                                                                        <p className="break-words" {...props}>
                                                                            {children}
                                                                        </p>
                                                                    );
                                                                },
                                                            }}
                                                        >
                                                            {/* CSS Classes für katex: 'katex', 'katex-html', 'katex-display'*/}
                                                            {segment.data
                                                                .replace(
                                                                    /\\\[(.*?)\\\]/g,
                                                                    (_: string, equation: string) => `$$${equation}$$`,
                                                                )
                                                                .replace(
                                                                    /\\\((.*?)\\\)/g,
                                                                    (_: string, equation: string) => `$${equation}$`,
                                                                )}
                                                        </ReactMarkdown>
                                                    </div>
                                                );
                                            }
                                        })}
                                        {contentChanged && (
                                            <div className={`mb-2 ${sources && sources.length > 0 ? 'mt-5' : ''}`}>
                                                <span className="assistant-dlp-tooltip">
                                                    <Badge variant="info">
                                                        {i18n.translate({
                                                            id: 'chatMessage.assistentDLP.badge',
                                                            sourceName: 'Chat',
                                                        })}
                                                    </Badge>
                                                </span>
                                                <Tooltip
                                                    className="shadow-xl p-2"
                                                    style={{
                                                        maxWidth: '440px',
                                                        width: '100%',
                                                        borderRadius: '0.5rem',
                                                        backgroundColor: '#001926',
                                                        zIndex: 9999,
                                                    }}
                                                    anchorSelect=".assistant-dlp-tooltip"
                                                    content={i18n.translate({
                                                        id: 'chatMessage.assistentDLP.tooltip',
                                                        sourceName: 'Chat',
                                                    })}
                                                />
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        {identifier !== editableMessage ? (
                                            <>
                                                <p
                                                    className="break-words"
                                                    dangerouslySetInnerHTML={{
                                                        __html: originalContent
                                                            .replace(/&/g, '&amp;')
                                                            .replace(/</g, '&lt;')
                                                            .replace(/>/g, '&gt;')
                                                            .replace(/"/g, '&quot;')
                                                            .replace(/'/g, '&#039;')
                                                            .replace(/\n/g, '<br />'),
                                                    }}
                                                />
                                                <div className="flex mb-2">
                                                    {contentChanged && (
                                                        <div className="flex items-center">
                                                            <span className="anchor-element-class">
                                                                <Badge variant="info">
                                                                    {i18n.translate({
                                                                        id: 'chatMessage.modifiedDLP',
                                                                        sourceName: 'Chat',
                                                                    })}
                                                                </Badge>
                                                            </span>
                                                            <Tooltip
                                                                className="shadow-xl p-2"
                                                                style={{
                                                                    maxWidth: '440px',
                                                                    width: '100%',
                                                                    borderRadius: '0.5rem',
                                                                    backgroundColor: '#001926',
                                                                    zIndex: 9999,
                                                                }}
                                                                anchorSelect=".anchor-element-class"
                                                                content={i18n.translate({
                                                                    id: 'chatMessage.contentChanged',
                                                                    sourceName: 'Chat',
                                                                })}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <MessageEditor
                                                    originalContent={originalContent}
                                                    editMessageHandler={editMessageHandler}
                                                    identifier={identifier}
                                                    setEditableMessage={setEditableMessage}
                                                    setEditedChatMessage={setEditedChatMessage}
                                                />
                                            </>
                                        )}
                                    </>
                                )}
                                {isLastMessage &&
                                    role === ChatMessageRole.assistant &&
                                    isSending && 
                                    (loadingAnimation.state !== 'closed' || !hasAssistantCode) && (
                                    <LoadingAnimation 
                                        loadingAnimation={
                                            !hasAssistantCode && loadingAnimation.state === 'closed'
                                                ? {
                                                    state: 'open',
                                                    percentage: 25,
                                                    icon: 'Code',
                                                    content: i18n.translate({
                                                        id: 'codeInterpreter.workInProgress',
                                                        sourceName: 'Chat',
                                                    }),
                                                }
                                                : loadingAnimation
                                        } 
                                    />
                                )}
                                {sources && sources.length > 0 && content && content.trim() !== '' && (
                                    <div>
                                        <Button
                                            variant="secondary"
                                            className="mt-5 mb-3"
                                            onClick={() => {
                                                messageSourceIdentifier !== identifier
                                                    ? dispatch(
                                                          setMessageSource({
                                                              messageIdentifier: identifier,
                                                              content: sources,
                                                          } as StoreMessageSource),
                                                      )
                                                    : dispatch(
                                                          setMessageSource({
                                                              messageIdentifier: '',
                                                              content: [{ title: '', description: '', url: '' }],
                                                          } as StoreMessageSource),
                                                      );
                                            }}
                                        >
                                            {i18n.translate({
                                                id:
                                                    messageSourceIdentifier !== identifier
                                                        ? 'chatMessage.sources.button.show'
                                                        : 'chatMessage.sources.button.hide',
                                                sourceName: 'Chat',
                                            })}
                                        </Button>
                                    </div>
                                )}
                            </>
                        ) : (
                            <>
                                {isLastMessage && role === ChatMessageRole.assistant && isSending && (
                                    <LoadingAnimation
                                        loadingAnimation={
                                            loadingAnimation.state !== 'closed'
                                                ? loadingAnimation
                                                : {
                                                      state: 'open',
                                                      percentage: 25,
                                                      icon: 'Spinner' as IconName,
                                                      content: i18n.translate({
                                                          id: 'openaiassistant.working',
                                                          sourceName: 'LoadingAnimation',
                                                      }),
                                                  }
                                        }
                                    />
                                )}
                            </>
                        )}
                        {role === 'assistant' ? (
                            <>
                                <div
                                    className={!isSending ? 'visible sm:invisible sm:group-hover:visible' : 'invisible'}
                                >
                                    <span
                                        className="text-chat-editIconColor inline-block mr-2"
                                        onClick={() => {
                                            copy(content);
                                            setFullCopied(true);
                                            setTimeout(() => {
                                                setFullCopied(false);
                                            }, 1500);
                                        }}
                                    >
                                        <Button
                                            variant="secondaryRound"
                                            className="cursor-pointer h-[26px] w-[26px] md:h-[26px] md:w-[26px]"
                                        >
                                            {fullCopied ? (
                                                <CheckIcon size={16} className="inline" />
                                            ) : (
                                                <CopyIcon size={16} className="inline" />
                                            )}
                                        </Button>
                                    </span>

                                    <AssistantAudioControls
                                        identifier={identifier}
                                        text={content}
                                        isSending={isSending}
                                        variant={windowWidth < breakpointSM ? 'mobile' : 'desktop'}
                                    />
                                </div>
                            </>
                        ) : (
                            identifier !== editableMessage && (
                                <div className={`invisible sm:group-hover:visible`}>
                                    <span className="text-chat-editIconColor inline-block mr-2">
                                        <Button
                                            variant="secondaryRound"
                                            className="cursor-pointer h-[26px] w-[26px] md:h-[26px] md:w-[26px]"
                                            onClick={handleEditClick}
                                        >
                                            <PencilSimpleIcon size={16} className="inline" />
                                        </Button>
                                    </span>
                                    <span className="text-chat-editIconColor inline-block">
                                        <Button
                                            variant="secondaryRound"
                                            className="cursor-pointer h-[26px] w-[26px] md:h-[26px] md:w-[26px]"
                                            onClick={handleRetryClick}
                                        >
                                            <ArrowClockwiseIcon size={16} className="inline" />
                                        </Button>
                                    </span>
                                </div>
                            )
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default memo(ChatMessage, (prevProps, nextProps) => {
    return (
        prevProps.identifier === nextProps.identifier &&
        prevProps.content === nextProps.content &&
        prevProps.contentChanged === nextProps.contentChanged &&
        prevProps.images === nextProps.images &&
        prevProps.files === nextProps.files &&
        prevProps.isLastMessage === nextProps.isLastMessage &&
        prevProps.role === nextProps.role &&
        prevProps.dlpActive === nextProps.dlpActive &&
        prevProps.loadingAnimation === nextProps.loadingAnimation &&
        prevProps.isPreviewPrompt === nextProps.isPreviewPrompt &&
        prevProps.isSending === nextProps.isSending &&
        prevProps.extraHeight === nextProps.extraHeight &&
        prevProps.shouldGrow === nextProps.shouldGrow &&
        prevProps.assistantName === nextProps.assistantName &&
        prevProps.assistantImage === nextProps.assistantImage &&
        prevProps.originalContent === nextProps.originalContent &&
        prevProps.dlpState === nextProps.dlpState &&
        prevProps.sources === nextProps.sources &&
        prevProps.chatId === nextProps.chatId
    );
});
